import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { getOrg } from '../../controllers/org';
import { getStatementsReporte, setStatementsToken } from '../../controllers/statements';
import { useAuth } from '../auth/AuthProvider';

const MONTHS = [
  { value: 8, short: 'Ago' },
  { value: 9, short: 'Sep' },
  { value: 10, short: 'Oct' },
  { value: 11, short: 'Nov' },
  { value: 12, short: 'Dic' },
  { value: 1, short: 'Ene' },
  { value: 2, short: 'Feb' },
  { value: 3, short: 'Mar' },
  { value: 4, short: 'Abr' },
  { value: 5, short: 'May' },
  { value: 6, short: 'Jun' },
  { value: 7, short: 'Jul' },
];

function getYearOptions() {
  const now = new Date();
  const maxCiclo = now.getMonth() + 1 >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const years = [];
  for (let y = maxCiclo; y >= 2025; y--) {
    years.push(y);
  }
  return years;
}

function StatementsReporte() {
  const { token } = useAuth();
  const now = new Date();
  const currentCiclo = now.getMonth() + 1 >= 8 ? now.getFullYear() : now.getFullYear() - 1;

  const [org, setOrg] = useState();
  const [report, setReport] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  // Filter state
  const [ciclo, setCiclo] = useState(currentCiclo);
  const [regionId, setRegionId] = useState('');
  const [diocesisId, setDiocesisId] = useState('');
  const [comunidadId, setComunidadId] = useState('');

  setStatementsToken(token);

  useEffect(() => {
    getOrg().then(o => setOrg(o));
    loadReport({ ciclo: currentCiclo });
  }, []);

  // Reset cascading filters
  useEffect(() => {
    setDiocesisId('');
    setComunidadId('');
  }, [regionId]);

  useEffect(() => {
    setComunidadId('');
  }, [diocesisId]);

  const diocesisList = org?.diocesis?.filter(d =>
    !regionId || d.region_id === parseInt(regionId)
  ) || [];

  const comunidadesList = org?.comunidades?.filter(c =>
    (!diocesisId || c.diocesis_id === parseInt(diocesisId)) &&
    (!regionId || diocesisList.some(d => d.diocesis_id === c.diocesis_id))
  ) || [];

  async function loadReport(filters) {
    setLoading(true);
    setError(undefined);
    try {
      const data = await getStatementsReporte(filters);
      setReport(data);
    } catch (err) {
      setError(err?.message || 'Error al cargar el reporte.');
    }
    setLoading(false);
  }

  async function handleSearch(e) {
    e.preventDefault();
    loadReport({
      ciclo,
      regionId: regionId || undefined,
      diocesisId: diocesisId || undefined,
      comunidadId: comunidadId || undefined,
    });
  }

  function handleClear() {
    setCiclo(currentCiclo);
    setRegionId('');
    setDiocesisId('');
    setComunidadId('');
    loadReport({ ciclo: currentCiclo });
  }

  const yearOptions = getYearOptions();

  return (
    <div className="mt-3">
      <h5>Reporte de Estados de Cuenta por Comunidad</h5>

      <Form onSubmit={handleSearch} className="mb-3">
        <Row className="g-2 align-items-end">
          <Col md={2}>
            <Form.Label>Ciclo</Form.Label>
            <Form.Select value={ciclo} onChange={e => setCiclo(parseInt(e.target.value))}>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y} - {y + 1}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Región</Form.Label>
            <Form.Select value={regionId} onChange={e => setRegionId(e.target.value)}>
              <option value="">Todas las regiones</option>
              {org?.regiones?.map(r => (
                <option key={r.region_id} value={r.region_id}>{r.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Diócesis</Form.Label>
            <Form.Select value={diocesisId} onChange={e => setDiocesisId(e.target.value)} disabled={!regionId}>
              <option value="">Todas las diócesis</option>
              {diocesisList.map(d => (
                <option key={d.diocesis_id} value={d.diocesis_id}>{d.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Comunidad</Form.Label>
            <Form.Select value={comunidadId} onChange={e => setComunidadId(e.target.value)} disabled={!diocesisId}>
              <option value="">Todas</option>
              {comunidadesList.map(c => (
                <option key={c.comunidad_id} value={c.comunidad_id}>{c.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md="auto" className="d-flex gap-2">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <Spinner size="sm" animation="border" /> : 'Buscar'}
            </Button>
            <Button type="button" variant="outline-secondary" onClick={handleClear} disabled={loading}>
              Limpiar
            </Button>
          </Col>
        </Row>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {report && (
        <>
          <p className="text-muted small mb-2">
            {report.comunidades.length} comunidad(es) encontrada(s) para el ciclo {report.ciclo}.
          </p>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="statements-report-table">
              <thead className="table-light">
                <tr>
                  <th>Comunidad</th>
                  <th>Diócesis</th>
                  <th>Región</th>
                  {MONTHS.map(m => (
                    <th key={m.value} className="text-center">{m.short}</th>
                  ))}
                  <th className="text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {report.comunidades.map(c => {
                  const uploadedSet = new Set(c.uploadedMonths);
                  const variant = c.totalStatements === 12 ? 'success' : c.totalStatements > 0 ? 'warning' : 'danger';
                  return (
                    <tr key={c.comunidadId}>
                      <td>{c.label}</td>
                      <td>{c.diocesisLabel || '-'}</td>
                      <td>{c.regionLabel || '-'}</td>
                      {MONTHS.map(m => (
                        <td key={m.value} className="text-center">
                          {uploadedSet.has(m.value)
                            ? <a
                                href={c.statementUrls?.[m.value]?.url}
                                target="_blank"
                                rel="noreferrer"
                                title={c.statementUrls?.[m.value]?.fileName}
                                className="text-success fw-bold text-decoration-none"
                              >✓</a>
                            : <span className="text-danger" title="Faltante">–</span>
                          }
                        </td>
                      ))}
                      <td className="text-center">
                        <Badge bg={variant}>{c.totalStatements}/12</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export default StatementsReporte;
